import { type ReactElement } from 'react';
import { s } from '@/theme/tokens';
import { TableCell } from '@/ui/primitives';
import { TableV2MetricChip } from './TableV2MetricChip';
import { TableV2SortableHeader } from './TableV2Primitives';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort } from '@/ui/composites/grid/gridUtils';
import type { MetricThresholdGroup } from '@flowqa/shared';

export type TableV2MetricSortField =
  | 'executionAverageDurationMs'
  | 'executionFlakeScore'
  | 'executionFrequencyPerDay'
  | 'executionPassRate';

type TableV2MetricValues = {
  averageDurationMs?: number | null;
  flakeScore?: number | null;
  frequencyPerDay?: number | null;
  passRate?: number | null;
};

export const TableV2MetricHeaders = ({
  sort,
  sortingDisabled = false,
  visibleMetricKeys,
  onSortChange,
}: {
  sort: ApiSort;
  sortingDisabled?: boolean;
  visibleMetricKeys?: MetricThresholdGroupKey[];
  onSortChange: (field: TableV2MetricSortField) => void;
}): ReactElement => {
  const visibleMetricKeySet = new Set(visibleMetricKeys);
  const shouldShowMetric = (key: MetricThresholdGroupKey): boolean =>
    visibleMetricKeys === undefined || visibleMetricKeySet.has(key);

  return (
    <>
      {shouldShowMetric('passRate') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2SortableHeader
            activeSort={sort}
            disabled={sortingDisabled}
            field="executionPassRate"
            label="Success Rate"
            onSortChange={onSortChange}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('frequency') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2SortableHeader
            activeSort={sort}
            disabled={sortingDisabled}
            field="executionFrequencyPerDay"
            label="Frequency"
            onSortChange={onSortChange}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('avgDuration') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2SortableHeader
            activeSort={sort}
            disabled={sortingDisabled}
            field="executionAverageDurationMs"
            label="Avg. Duration"
            onSortChange={onSortChange}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('flakeScore') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2SortableHeader
            activeSort={sort}
            disabled={sortingDisabled}
            field="executionFlakeScore"
            label="Flake Score"
            onSortChange={onSortChange}
          />
        </TableCell>
      ) : null}
    </>
  );
};

export const TableV2MetricCells = ({
  metrics,
  thresholds,
  visibleMetricKeys,
}: {
  metrics: TableV2MetricValues | null;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys?: MetricThresholdGroupKey[];
}): ReactElement => {
  const visibleMetricKeySet = new Set(visibleMetricKeys);
  const shouldShowMetric = (key: MetricThresholdGroupKey): boolean =>
    visibleMetricKeys === undefined || visibleMetricKeySet.has(key);

  return (
    <>
      {shouldShowMetric('passRate') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2MetricChip
            kind="passRate"
            threshold={thresholds.passRate}
            value={metrics?.passRate ?? null}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('frequency') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2MetricChip
            kind="frequency"
            threshold={thresholds.frequency}
            value={metrics?.frequencyPerDay ?? null}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('avgDuration') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2MetricChip
            kind="avgDuration"
            threshold={thresholds.avgDuration}
            value={metrics?.averageDurationMs ?? null}
          />
        </TableCell>
      ) : null}
      {shouldShowMetric('flakeScore') ? (
        <TableCell sx={{ pl: s('none'), textAlign: 'left' }}>
          <TableV2MetricChip
            kind="flakeScore"
            threshold={thresholds.flakeScore}
            value={metrics?.flakeScore ?? null}
          />
        </TableCell>
      ) : null}
    </>
  );
};
